import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Tooltip, IconButton, FormHelperText } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Alert } from "@mui/material";
import { changePassword, getProfile } from "../../http/auth";
import { useDispatch } from "react-redux";
import { saveUserDetails, showToast } from "../../store/slices/common";
import { useNavigate } from "react-router-dom";
import routePaths from '../../router/route-paths';
import PageLayout from "../../components/PageLayout";
import { usePermissions } from "../../hooks/usePermissions";
import { getUserInfo, storeUserInfo } from "../../utils/auth-token-utils/cookie";

export default function ChangePassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, mustChangePassword } = usePermissions();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordGuidelines = `Must be a minimum of 8 characters in length.
Must contain at least one uppercase letter, one lowercase letter, one number (0-9), and one special character.`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    const data = new FormData(event.currentTarget);

    const oldPassword = data.get("oldPassword");
    const newPassword = data.get("newPassword");
    const confirmPassword = data.get("confirmPassword");

    if (newPassword === oldPassword) {
      setErrors((prev) => ({ ...prev, newPassword: "New password cannot be same as old password" }));
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "New password not matching with confirm password" }));
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setErrors((prev) => ({ ...prev, newPassword: "Please provide valid password" }));
      return;
    }

    try {
      const res = await changePassword({ oldPassword, newPassword });
      if (res.status === 200) {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        dispatch(showToast({ color: "success", msg: "Password changed successfully" }));
        // The temporary-password gate (mustChangePassword) is cleared server
        // side; refresh the profile so ProtectedRoute stops redirecting here.
        try {
          const profile = await getProfile();
          const userData = profile.data.data?.userDetails;
          dispatch(saveUserDetails(userData || { ...user, mustChangePassword: false }));
        } catch (e) {
          dispatch(saveUserDetails({ ...user, mustChangePassword: false }));
        }
        // "Remember me" keeps the login payload in localStorage and rehydrates
        // Redux from it on refresh; clear the flag there too.
        const remembered = getUserInfo();
        if (remembered?.profile?.mustChangePassword) {
          storeUserInfo({
            ...remembered,
            profile: { ...remembered.profile, mustChangePassword: false },
          });
        }
        navigate(routePaths.DASHBOARD);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" })); // Reset error for the changed field
    if (name === "oldPassword") setOldPassword(value);
    else if (name === "newPassword") setNewPassword(value);
    else if (name === "confirmPassword") setConfirmPassword(value);
  };

  const handleClickShowPassword = (field) => {
    switch (field) {
      case 'oldPassword':
        setShowOldPassword((prev) => !prev);
        break;
      case 'newPassword':
        setShowNewPassword((prev) => !prev);
        break;
      case 'confirmPassword':
        setShowConfirmPassword((prev) => !prev);
        break;
      default:
        break;
    }
  };

  return (
    <PageLayout title="Change Password">
      <CssBaseline />
      <Box sx={{display: "flex", flexDirection: "column",width:{
        xs:'100%',
          sm:'300px',
          md:'350px',
          lg:'550px',
          xl:'550px'
        } }}>
        <Avatar sx={{bgcolor: "secondary.main",margin:"0 auto 20px" }}>
          <LockOutlinedIcon />
        </Avatar>
        {mustChangePassword && (
          <Alert severity="info" sx={{ borderRadius: "6px" }}>
            You signed in with a temporary password. Please set your own password
            before continuing. Enter the temporary password as the old password.
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{marginTop:{
          xs:'15px',
            sm:'20px',
            md:'20px',
            lg:'30px',
            xl:'30px'
          } }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="oldPassword"
            label="Old Password"
            type={showOldPassword ? "text" : "password"}
            id="oldPassword"
            autoComplete="current-password"
            value={oldPassword}
            onChange={handleChange}
            error={!!errors.oldPassword}
            helperText={errors.oldPassword}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: '6px',
              },
              '& .MuiOutlinedInput-notchedOutline':{
                borderColor:'#142C5C'
              },
              "& input": {
                fontSize: "18px",
                padding: "14px",
                color: "#142C5C",
              },
              "& .MuiFormLabel-root.Mui-focused": { color: "#142C5C" },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#142C5C",
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton sx={{
                  marginRight:'1px'
                }}
                  onClick={() => handleClickShowPassword('oldPassword')}
                  edge="end"
                >
                  {showOldPassword ? <Visibility sx={{
                      fontSize:'20px'
                    }} /> : <VisibilityOff sx={{
                      fontSize:'20px'
                    }} />}
                </IconButton>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            id="newPassword"
            autoComplete="new-password"
            value={newPassword}
            onChange={handleChange}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: '6px',
              },
              '& .MuiOutlinedInput-notchedOutline':{
                borderColor:'#142C5C'
              },
              "& input": {
                fontSize: "18px",
                padding: "14px",
                color: "#142C5C",
              },
              "& .MuiFormLabel-root.Mui-focused": { color: "#142C5C" },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#142C5C",
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                    sx={{
                  marginRight:'1px'
                }}
                  onClick={() => handleClickShowPassword('newPassword')}
                  edge="end"
                >
                  {showNewPassword ? <Visibility sx={{
                      fontSize:'20px'
                    }}/> : <VisibilityOff sx={{
                      fontSize:'20px'
                    }}/>}
                </IconButton>
              ),
            }}
          />
          {errors.newPassword === "Please provide valid password" && (
            <FormHelperText error>
              {errors.newPassword}
              <Tooltip title={passwordGuidelines}>
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </FormHelperText>
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: '6px',
              },
              '& .MuiOutlinedInput-notchedOutline':{
                borderColor:'#142C5C'
              },
              "& input": {
                fontSize: "18px",
                padding: "14px",
                color: "#142C5C",
              },
              "& .MuiFormLabel-root.Mui-focused": { color: "#142C5C" },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#142C5C",
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                    sx={{
                  marginRight:'1px'
                }}
                  onClick={() => handleClickShowPassword('confirmPassword')}
                  edge="end"
                >
                  {showConfirmPassword ? <Visibility sx={{
                      fontSize:'20px'
                    }} /> : <VisibilityOff sx={{
                      fontSize:'20px'
                    }} />}
                </IconButton>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              height:'55px',
              background: "#142C5C",
              borderRadius: "80px",
              "&:hover": { background: "#142C5C" },
            }}
          >
            Change Password
          </Button>
        </Box>
      </Box>
    </PageLayout>
  );
}
